/**
 * How an ended session came out: Succeeded, Failed or Stopped, from its last result (the
 * agent's ext.failure, the error Claude Code printed instead of an answer, the canonical
 * state). A session that failed and was then continued (a retry, resume, message or wake)
 * and finished cleanly reads Succeeded: its newest result wins. Pure, so it is tested.
 */
import type { Session } from '@orgus/protocol';
import { SESSION_ERROR_RE, sessionFailureExt, sessionStateOf } from '@orgus/protocol';

export type SessionOutcome = 'succeeded' | 'failed' | 'stopped';

export const OUTCOME_LABEL: Record<SessionOutcome, string> = { succeeded: 'Succeeded', failed: 'Failed', stopped: 'Stopped' };

export type OutcomeInput = Pick<Session, 'status'> & Partial<Pick<Session, 'lastText' | 'lastActivity' | 'turns' | 'toolCalls' | 'state' | 'ext'>>;

/** The outcome of an ended session, or null while it is live. */
export function sessionOutcome(s: OutcomeInput): SessionOutcome | null {
  if (s.status !== 'ended') return null;
  const state = sessionStateOf({ status: s.status, turns: s.turns ?? 0, toolCalls: s.toolCalls ?? 0, lastText: s.lastText ?? '', state: s.state, ext: s.ext } as Session);
  if (state === 'stopped') return 'stopped';
  if (state !== 'failed') return 'succeeded';
  // A failure older than the session's last activity, with clean last words since: a later
  // turn (the retry) finished, so the newest result is a success.
  const f = sessionFailureExt(s);
  const clean = !!s.lastText && !SESSION_ERROR_RE.test(s.lastText);
  if (f && clean && s.lastActivity !== undefined && s.lastActivity > f.at) return 'succeeded';
  return 'failed';
}
